import { ensureDefined } from '@e-socna/utils';
import type { CivilityId, Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type {
  ResolverHookResult,
  ResolverParameters,
} from '../../types/resolvers.type';

import { useMissionQuickAccess } from './useMissionQuickAccess.hook';

interface Result {
  civility: {
    _id: Id;
    abbreviation: string;
    isPlural: boolean;
    label: string;
    useWhenUnknown: boolean;
  };
  fullName: string | undefined;
}

export const useQuotationContact = (
  parameters: ResolverParameters,
): ResolverHookResult<Result> => {
  const { civilities, civilitiesIdMap } = useReferencesContext();

  const quickAccess = useMissionQuickAccess(parameters);

  if (!quickAccess.isSuccess) {
    return {
      ...quickAccess,
      data: undefined,
    };
  }

  const {
    data: { consolidation },
  } = ensureDefined({
    data: quickAccess.data,
  });

  const { quotationReceiverActor } = ensureDefined({
    quotationReceiverActor: consolidation.quotationReceiverActor,
  });

  const successResult = {
    error: null,
    isSuccess: true,
    isLoading: false,
  };

  switch (quotationReceiverActor.__typename) {
    case 'ActorPerson': {
      const {
        thirdParty: { civilityId, fullName },
      } = quotationReceiverActor;

      const { civility } = ensureDefined({
        civility: civilitiesIdMap[civilityId as CivilityId],
      });

      return { ...successResult, data: { civility, fullName } };
    }
    case 'ActorOrganization': {
      const { quotationContact } = quotationReceiverActor.consolidation;

      if (!quotationContact) {
        const { civility } = ensureDefined({
          civility: civilities.find((civility) => civility.useWhenUnknown),
        });

        return { ...successResult, data: { civility, fullName: undefined } };
      }

      const {
        person: { civilityId, fullName },
      } = quotationContact;

      const { civility } = ensureDefined({
        civility: civilitiesIdMap[civilityId as CivilityId],
      });

      return { ...successResult, data: { civility, fullName } };
    }
    default: {
      throw new Error('Unhandled quotationReceiverActor typename');
    }
  }
};
